export interface AnnotationPoint {
  x: number; // normalized 0..1 relative to page width
  y: number; // normalized 0..1 relative to page height
}

export interface StrokeAnnotation {
  id: string;
  type: 'stroke';
  color: string;
  width: number; // normalized fraction of page width, so it scales with zoom
  points: AnnotationPoint[];
}

export interface TextAnnotation {
  id: string;
  type: 'text';
  color: string;
  x: number; // normalized top-left position
  y: number;
  text: string;
  fontSize: number; // normalized fraction of page width
}

export interface HighlightAnnotation {
  id: string;
  type: 'highlight';
  color: string;
  x: number; // normalized top-left position
  y: number;
  w: number; // normalized width
  h: number; // normalized height
}

export type Annotation =
  | StrokeAnnotation
  | TextAnnotation
  | HighlightAnnotation;

// key = page number, value = annotations on that page
export type PageAnnotations = Record<number, Annotation[]>;

// key = document url
export type DocumentAnnotations = Record<string, PageAnnotations>;

const STORAGE_KEY = 'geneo_pdf_annotations_v2';

export const ANNOTATION_COLORS = [
  '#e53935',
  '#1e88e5',
  '#43a047',
  '#fdd835',
  '#000000',
];

// stroke width and font size as a fraction of page width so they scale with zoom
export const ANNOTATION_STROKE_WIDTH = 0.004;
export const ANNOTATION_FONT_SIZE = 0.022;

export const createAnnotationId = (): string =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const readAll = (): DocumentAnnotations => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as DocumentAnnotations) : {};
  } catch (error) {
    console.error('Failed to read pdf annotations from localStorage', error);
    return {};
  }
};

const writeAll = (data: DocumentAnnotations): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to persist pdf annotations to localStorage', error);
  }
};

export const getDocumentAnnotations = (url: string): PageAnnotations => {
  const all = readAll();
  return all[url] || {};
};

export const saveDocumentAnnotations = (
  url: string,
  pages: PageAnnotations
): void => {
  const all = readAll();
  const hasAnnotations = Object.values(pages).some(
    (annotations) => annotations.length > 0
  );
  if (hasAnnotations) {
    all[url] = pages;
  } else {
    delete all[url];
  }
  writeAll(all);
};
